import React, { useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { AttendeeBody, AttendeeKind, EventDetailDTO } from "../../../../models/EventDTO";
import S from "./strings";
import BuscarAlumnaEvento from "./buscarAlumnaEvento";
import QueVaAPasar from "./queVaAPasar";
import { Modal, dinero, errorText, useRolDinero } from "./comun";

const KINDS: AttendeeKind[] = ["graduada", "participante", "especial", "externo"];
const SE_COBRA: AttendeeKind[] = ["graduada", "participante"];

/**
 * «Más › Agregar Otro» (Módulos Claros, 2026-11-01): una graduada que no salió en Agregar Graduadas, una participante, o un invitado
 * especial / externo. Los invitados de una graduada se agregan desde su fila (+ Invitados). Antes de agregar: «Qué Va a Pasar».
 */
const AgregarOtro: React.FC<{ campusId: number; evento: EventDetailDTO; onClose: () => void; onDone: (t: string) => void }> = ({ campusId, evento, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	const rolDinero = useRolDinero(campusId);
	const [kind, setKind] = useState<AttendeeKind>("especial");
	const [alumna, setAlumna] = useState<{ id: number; name: string } | null>(null);
	const [name, setName] = useState<string>("");
	const [tableNo, setTableNo] = useState<string>("");
	const [cobra, setCobra] = useState<boolean>(true);
	const [price, setPrice] = useState<string>("");
	const [priceNote, setPriceNote] = useState<string>("");
	const [revisar, setRevisar] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const precioEvento = SE_COBRA.includes(kind) ? evento.packagePrice : kind === "especial" ? 0 : evento.extraGuestPrice;
	const conPrecio = kind === "externo" || (SE_COBRA.includes(kind) && cobra);
	const precioDistinto = conPrecio && price.trim() !== "" && !isNaN(Number(price)) && Math.abs(Number(price) - precioEvento) > 0.004;
	const valido = (SE_COBRA.includes(kind) ? !!alumna : (!!alumna || name.trim().length > 1)) && (!precioDistinto || priceNote.trim().length > 1);
	const cuerpo = (): Omit<AttendeeBody, "eventId"> => ({
		kind, studentId: alumna ? alumna.id : undefined, name: alumna ? undefined : name.trim() || undefined, tableNo: tableNo.trim() || undefined,
		charge: SE_COBRA.includes(kind) ? cobra : undefined, price: conPrecio && price.trim() !== "" ? Number(price) : undefined, priceNote: precioDistinto ? priceNote.trim() : undefined
	});
	const guardar = () => {
		setBusy(true); setError(null);
		http.eventsService.addAttendee(campusId, { eventId: evento.id, ...cuerpo() } as AttendeeBody)
			.then(() => onDone(S.agregadoOk(1)))
			.catch(e => { setError(errorText(e)); setBusy(false); setRevisar(false); });
	};

	return (
		<Modal title={S.agregarOtro} onClose={() => !busy && onClose()} ancho={560}>
			{revisar ? (
				<QueVaAPasar campusId={campusId} busy={busy} confirmar={S.confirmar}
					pedir={() => http.eventsService.previewAdd(campusId, { eventId: evento.id, action: "attendees", items: [cuerpo()] })}
					onVolver={() => setRevisar(false)} onConfirmar={guardar} />
			) : <>
				<div className="wau-chips" role="group">
					{KINDS.map(k => <button key={k} type="button" className="wau-chip" aria-pressed={kind === k} onClick={() => { setKind(k); setAlumna(null); setPrice(""); setPriceNote(""); }}>{S.kinds[k]}</button>)}
				</div>
				{alumna ? <div className="wau-wa"><b>{alumna.name}</b><button type="button" className="wau-btn s mini" onClick={() => setAlumna(null)}>{S.quitar}</button></div>
					: <div style={{ marginBottom: 10 }}><BuscarAlumnaEvento campusId={campusId} eventId={evento.id} bloquearSiVa={SE_COBRA.includes(kind)} onPick={setAlumna} /></div>}
				{!SE_COBRA.includes(kind) && !alumna ? (
					<label className="wau-campo" style={{ marginBottom: 10, width: "100%" }}><span className="wau-lab">{S.colNombre}</span>
						<input className="wau-entrada" maxLength={150} value={name} onChange={e => setName(e.target.value)} /></label>
				) : null}
				<div className="wau-fila" style={{ marginBottom: 10, alignItems: "flex-end" }}>
					{SE_COBRA.includes(kind) ? <label className="wev-check"><input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.seCobra}</span></label> : null}
					{conPrecio ? (rolDinero ? (
						<label className="wau-campo" style={{ width: 150 }}><span className="wau-lab">{S.precio} ({evento.currency?.code || ""})</span>
							<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={price} placeholder={String(precioEvento)} onChange={e => setPrice(e.target.value)} /></label>
					) : <div className="wau-campo"><span className="wau-lab">{S.precioEvento}</span><span className="wau-num">{dinero(evento.currency, precioEvento)}</span></div>) : null}
					<label className="wau-campo" style={{ width: 90 }}><span className="wau-lab">{S.mesa}</span>
						<input className="wau-entrada wau-num" maxLength={10} value={tableNo} onChange={e => setTableNo(e.target.value)} /></label>
				</div>
				{precioDistinto ? <label className="wau-campo" style={{ marginBottom: 10, width: "100%" }}><span className="wau-lab">{S.motivo}</span>
					<input className="wau-entrada" maxLength={255} value={priceNote} onChange={e => setPriceNote(e.target.value)} /></label> : null}
				{error ? <p className="wau-adv">{error}</p> : null}
				<div className="wau-acciones">
					<button type="button" className="wau-btn s" onClick={onClose}>{S.volver}</button>
					<button type="button" className="wau-btn p" onClick={() => setRevisar(true)} disabled={!valido}>{S.revisar}</button>
				</div>
			</>}
		</Modal>
	);
};

export default AgregarOtro;
